import type { Transport, PortLike } from "./types";

/** Normalizes Worker, SharedWorker, BroadcastChannel, and raw ports into a uniform Transport interface. */
// biome-ignore lint/suspicious/noExplicitAny: Because
export class PortAdapter<T = any, R = any> implements Transport<T> {
  raw!: R;
  port!: PortLike;

  onMessage!: Transport<T>["onMessage"];
  onError!: Transport<T>["onError"];
  send!: Transport<T>["send"];

  start?: () => void;
  close?: () => void;

  static from = <T, R extends Worker | SharedWorker | PortAdapter<T, R> | PortLike | BroadcastChannel | Transport<T>>(
    w: R,
  ): PortAdapter<T, R> => {
    if (w instanceof PortAdapter) return w;
    if (typeof Worker !== "undefined" && w instanceof Worker) return PortAdapter.fromWebWorker(w) as PortAdapter<T, R>;
    if (typeof SharedWorker !== "undefined" && w instanceof SharedWorker)
      return PortAdapter.fromSharedWorker(w) as PortAdapter<T, R>;
    if (typeof BroadcastChannel !== "undefined" && w instanceof BroadcastChannel)
      return PortAdapter.fromBroadcastChannel(w) as PortAdapter<T, R>;
    if ("addEventListener" in w) return PortAdapter.fromGenericPort(w as PortLike) as PortAdapter<T, R>;
    if ("onMessage" in w) return PortAdapter.fromTransport(w as Transport) as PortAdapter<T, R>;
    throw new Error("Invalid transport");
  };

  static fromTransport = (
    t: Transport & { onError?: (cb: (error: Event | ErrorEvent) => void) => () => void },
  ): PortAdapter => {
    return buildAdapter({
      raw: t,
      port: portFromTransport(t),
      send: t.send,
    });
  };

  static fromWebWorker = <T>(worker: Worker): PortAdapter<T, Worker> => {
    return buildAdapter({
      raw: worker,
      port: worker,
      send: (message, transfer) => worker.postMessage(message, transfer ? { transfer } : undefined),
      close: () => worker.terminate(),
    });
  };

  static fromSharedWorker = <T>(sharedWorker: SharedWorker): PortAdapter<T, SharedWorker> => {
    return buildAdapter({
      raw: sharedWorker,
      port: sharedWorker.port,
      send: (message, transfer) => sharedWorker.port.postMessage(message, transfer ? { transfer } : undefined),
      close: () => sharedWorker.port.close(),
      start: () => sharedWorker.port.start(),
    });
  };

  static fromBroadcastChannel = <T>(channel: BroadcastChannel): PortAdapter<T, BroadcastChannel> => {
    return buildAdapter({
      raw: channel,
      port: channel,
      send: (message) => channel.postMessage(message),
      close: () => channel.close(),
    });
  };

  private static fromGenericPort = <T>(port: PortLike): PortAdapter<T, PortLike> => {
    return buildAdapter({
      raw: port,
      port: port,
      send: (message, transfer) => port.postMessage(message, transfer),
      close: () => port.close?.(),
    });
  };
}

interface PortSource<R> {
  raw: R;
  port: PortLike;
  send: Transport["send"];
  close?: () => void;
  start?: () => void;
}

function buildAdapter<T, R>(source: PortSource<R>): PortAdapter<T, R> {
  const t = new PortAdapter<T, R>();
  t.raw = source.raw;
  t.port = source.port;
  t.send = source.send;
  t.close = source.close;
  t.start = source.start;
  t.onMessage = (cb) => setupListener(source.port, "message", (e: MessageEvent) => cb(e.data));
  t.onError = (cb) => {
    const u1 = setupListener(source.raw as EventTarget, "error", cb);
    const u2 = setupListener(source.port, "messageerror", cb);
    return () => {
      u1();
      u2();
    };
  };
  return t;
}

const setupListener = (
  target: EventTarget,
  type: string,
  // biome-ignore lint/suspicious/noExplicitAny: Because
  handler: (e: any) => void,
): (() => void) => {
  target.addEventListener(type, handler);
  return () => target.removeEventListener(type, handler);
};

const portFromTransport = (t: Transport): PortLike => {
  const unsubs = new Map<EventListenerOrEventListenerObject, () => void>();
  return {
    dispatchEvent: () => false,
    postMessage: t.send,
    addEventListener: (type, listener) => {
      if (!listener) return;
      const cb = typeof listener === "function" ? listener : (e: Event) => listener.handleEvent(e);
      if (type === "message")
        unsubs.set(
          listener,
          t.onMessage((data) => cb({ data } as MessageEvent)),
        );
      else if ((type === "error" || type === "messageerror") && t.onError) unsubs.set(listener, t.onError(cb));
    },
    removeEventListener: (_type, listener) => {
      if (!listener) return;
      unsubs.get(listener)?.();
      unsubs.delete(listener);
    },
  };
};
